import '../Tenant/tenant-form.css';
import './apikey.css';

import { useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { useSnackbar } from 'material-ui-snackbar-provider';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

const CREATE_DIALOG_360_API_KEY_MUTATION = gql`
  mutation Dialog360ApiKey($input: D360ApiKeyInput) {
    dialog360ApiKey(input: $input) {
      api_key
    }
  }
`;

// ── Icons ──────────────────────────────────────────────────
const KeyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21 2-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

const CopyIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AlertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

// ── Component ──────────────────────────────────────────────
function ApiKeyPage() {
  const snackbar = useSnackbar();
  const history = useHistory();
  const [dialog360ApiKey, { loading: submitting }] = useMutation(
    CREATE_DIALOG_360_API_KEY_MUTATION
  );

  const [channelId, setChannelId] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setGeneratedKey('');

    dialog360ApiKey({
      variables: { input: { channelId } },
    })
      .then((r) => {
        const key = r?.data?.dialog360ApiKey?.api_key;
        if (key) {
          setGeneratedKey(key);
          snackbar.showMessage('API key generated successfully');
        } else {
          setError(
            'The server did not return an API key. Check the Channel ID and try again.'
          );
        }
      })
      .catch((err) => {
        const msg =
          err?.graphQLErrors?.[0]?.message ||
          err?.networkError?.message ||
          err?.message ||
          'Failed to generate API key. Please try again.';
        setError(msg);
      });
  };

  const handleCopy = () => {
    navigator.clipboard
      .writeText(generatedKey)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // fallback for browsers that block clipboard API
        const ta = document.createElement('textarea');
        ta.value = generatedKey;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
  };

  return (
    <div className="tenant-form-page">
      {/* Page header */}
      <div className="form-page-header">
        <div>
          <h1 className="form-page-title">API Keys</h1>
          <p
            className="page-subtitle"
            style={{ marginTop: 4, fontSize: 14, color: '#6b7280' }}
          >
            Generate API keys for your WhatsApp Dialog 360 channels.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-card" style={{ maxWidth: 560 }}>
          {/* Provider header */}
          <div className="apikey-card-header">
            <span className="apikey-provider-icon">
              <WhatsAppIcon />
            </span>
            <div>
              <div className="apikey-provider-name">WhatsApp Dialog 360</div>
              <div className="apikey-provider-desc">Generate a channel API key</div>
            </div>
          </div>

          <div className="form-card-body">
            <p className="form-section-title">Channel Configuration</p>

            {/* Channel ID */}
            <div className="form-group">
              <label className="form-label form-label-required" htmlFor="channelId">
                Channel ID
              </label>
              <input
                className="form-input"
                id="channelId"
                name="channelId"
                type="text"
                placeholder="e.g. whatsapp:+1234567890"
                value={channelId}
                onChange={(e) => {
                  setChannelId(e.target.value);
                  if (error) setError('');
                }}
                required
                autoComplete="off"
              />
              <span className="form-hint">
                The unique identifier for your Dialog 360 WhatsApp channel.
              </span>
            </div>

            {/* Error banner */}
            {error && (
              <div className="apikey-error">
                <AlertIcon />
                <span>{error}</span>
              </div>
            )}

            {/* Generated key result */}
            {generatedKey && !error && (
              <div className="apikey-result">
                <div className="apikey-result-header">
                  <span className="apikey-result-label">
                    <KeyIcon />
                    Generated API Key
                  </span>
                  <button
                    type="button"
                    className={`apikey-copy-btn ${copied ? 'copied' : ''}`}
                    onClick={handleCopy}
                  >
                    {copied ? (
                      <><CheckIcon /> Copied</>
                    ) : (
                      <><CopyIcon /> Copy</>
                    )}
                  </button>
                </div>
                <code className="apikey-value">{generatedKey}</code>
                <p className="apikey-warning">
                  Store this key securely — it will not be shown again after you
                  leave this page.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="form-card-footer">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => history.push('/tenants')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary-form"
              disabled={submitting}
            >
              {submitting
                ? 'Generating…'
                : generatedKey
                ? 'Regenerate'
                : 'Generate API Key'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default ApiKeyPage;
